import { T, useTolgee } from '@tolgee/react';
import { Modal, Tooltip } from 'antd';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { ArrowLeftSvg } from '../../../assets/icons';
import ArrowRightSvg from '../../../assets/icons/ArrowRightSvg';
import ClickSvg from '../../../assets/icons/ClickSvg';
import ProfileChecked from '../../../assets/icons/ProfileChecked';
import theme from '../../../assets/theme';
import { useMetrics } from '../../../hooks/useMetrics';
import { Response } from '../../../services/backendService/types';
import {
  hasStrengthBeenRevealed,
  setStrengthRevealed,
} from '../../../utils/liveGameStateStorage';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { MarkdownSlateViewer } from '../../_organisms/DesignerTabPane/AgentTab/MarkdownSlateViewer/MarkdownSlateViewer';
import { PinCodeNudger } from '../../SlideComponents/PinCodeNudger';
import LiveGameImage from '../LiveGameImage';

const HighlightedText = ({
  text,
  highlightText,
}: {
  text: string;
  highlightText: string;
}) => {
  if (!highlightText || !text) return <>{text}</>;
  const regex = new RegExp(
    `(${highlightText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
    'gi',
  );
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, index) =>
        regex.test(part) ? (
          <HighlightSpan key={index}>{part}</HighlightSpan>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </>
  );
};

const StrengthResponseReviewView = ({
  open,
  onDone,
  response,
  gameId,
  isDrawing,
  tenantId,
  isAuthenticated,
  onPrevious,
  onNext,
  responses,
  slideId,
}: {
  response?: Response | undefined;
  gameId: string;
  open: boolean;
  isDrawing?: boolean;
  onDone: () => void;
  tenantId?: string;
  isAuthenticated?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  responses?: Response[];
  slideId?: string;
}) => {
  const metrics = useMetrics();
  const router = useRouter();
  const { t } = useTolgee();
  const isResultsPage = router.pathname.endsWith('/results');
  const [isStrengthRevealed, setIsStrengthRevealed] = useState(false);

  useEffect(() => {
    if (!response?.id || !gameId) {
      setIsStrengthRevealed(false);
      return;
    }
    setIsStrengthRevealed(hasStrengthBeenRevealed(gameId, response.id));
  }, [gameId, response?.id]);

  useEffect(() => {
    if (open && response?.id && gameId && slideId) {
      window.dispatchEvent(
        new CustomEvent('aiModalOpened', {
          detail: { gameId, slideId },
        }),
      );
    }
  }, [open, response?.id, gameId, slideId]);

  const currentIndex = responses?.findIndex(r => r.id === response?.id) ?? -1;
  const isFirstResponse = currentIndex === 0;
  const isLastResponse = currentIndex === (responses?.length ?? 0) - 1;

  const responsesWithStrength = useMemo(
    () =>
      responses?.filter(r => r.strength?.strengthText || r.strength?.description) ?? [],
    [responses],
  );
  const lastTopPickIndex =
    responses?.findIndex(
      r => r.id === responsesWithStrength[responsesWithStrength.length - 1]?.id,
    ) ?? -1;

  const hasStrength = !!(
    response?.strength?.description || response?.strength?.strengthText
  );
  const shouldHighlight = !!(isStrengthRevealed && response?.strength?.strengthText);

  return (
    <ModalWrapper
      open={open}
      onCancel={onDone}
      onOk={onDone}
      footer={null}
      bodyStyle={{ padding: 0 }}
      closable={false}
      centered
      width="100vw"
    >
      <BodyContainer>
        {!isResultsPage && (
          <PinCodeNudger
            gameId={gameId}
            backgroundBrightness={0}
            useResponsiveSize={true}
          />
        )}
        <Title>
          {hasStrength ? (
            <T
              keyName="What makes this a good example?"
              defaultValue="What makes this a good example?"
            />
          ) : (
            ''
          )}
        </Title>

        <ContentWrapper>
          {isDrawing && response && tenantId ? (
            <CenteredImageContainer>
              <LiveGameImage
                tenantId={tenantId}
                modifiedAt={response.modifiedAt || 1}
                imageId={response.response}
                style={{ width: '100%', maxHeight: '85vh', background: 'white' }}
                objectFit="contain"
                boxShadow="0px 2px 20px rgba(0, 0, 0, 0.1)"
                fluid
                isAuthenticated={!!isAuthenticated}
              />
            </CenteredImageContainer>
          ) : hasStrength ? (
            <SplitLayout>
              <ResponseCardContainer>
                <ResponseCard>
                  <GreatPickTag>
                    <ProfileChecked />
                  </GreatPickTag>
                  <ResponseHeader>
                    <T keyName="Student Response" defaultValue="Student Response" />
                  </ResponseHeader>
                  <ResponseScrollInner>
                    {shouldHighlight ? (
                      <HighlightedText
                        text={response?.response || ''}
                        highlightText={response?.strength?.strengthText || ''}
                      />
                    ) : (
                      response?.response
                    )}
                  </ResponseScrollInner>
                </ResponseCard>
              </ResponseCardContainer>

              <StrengthCardContainer>
                <StrengthTabs>
                  <StrengthTab>{t('Strength')}</StrengthTab>
                </StrengthTabs>
                {!isStrengthRevealed && (
                  <RevealOverlay
                    onClick={() => {
                      if (!response?.id) return;
                      metrics.logEvent('StrengthResponseReviewView.StrengthRevealed');
                      setStrengthRevealed(gameId, response.id);
                      setIsStrengthRevealed(true);
                    }}
                  >
                    <RevealContent>
                      <IconWrapper color="#ffffff04" height="40%" width="40%">
                        <ClickSvg />
                      </IconWrapper>
                      <span>
                        <T keyName="Reveal" defaultValue="Reveal" />
                      </span>
                    </RevealContent>
                  </RevealOverlay>
                )}
                <StrengthContent>
                  <MarkdownSlateViewer
                    content={response?.strength?.description || ''}
                    fontSize={28}
                  />
                </StrengthContent>
              </StrengthCardContainer>
            </SplitLayout>
          ) : (
            <CenteredResponseWrapper>
              <ResponseCardContainer>
                <ResponseCard>
                  <ResponseHeader>
                    <T keyName="Student Response" defaultValue="Student Response" />
                  </ResponseHeader>
                  <ResponseScrollInner>{response?.response}</ResponseScrollInner>
                </ResponseCard>
              </ResponseCardContainer>
            </CenteredResponseWrapper>
          )}
        </ContentWrapper>

        {responses && responses.length > 1 && (
          <NavigationButtonsContainer>
            <Tooltip
              mouseEnterDelay={1}
              title={
                isFirstResponse
                  ? t('This is the first response', 'This is the first response')
                  : ''
              }
            >
              <NavigationButton
                icon={
                  <IconWrapper height="30px" width="30px" style={{ cursor: 'inherit' }}>
                    <ArrowLeftSvg />
                  </IconWrapper>
                }
                onClick={() => {
                  if (isFirstResponse) return;
                  onPrevious?.();
                }}
                $isDisabled={isFirstResponse}
              />
            </Tooltip>

            <DoneReviewingButton
              onClick={onDone}
              $isActive={currentIndex >= lastTopPickIndex && lastTopPickIndex !== -1}
            >
              <span>
                <T keyName="close" defaultValue="Close" />
              </span>
            </DoneReviewingButton>

            <Tooltip
              mouseEnterDelay={1}
              title={
                isLastResponse
                  ? t('thisIsTheLastResponse', 'This is the last response')
                  : ''
              }
            >
              <NavigationButton
                $isDisabled={isLastResponse}
                $isOrange={
                  !(currentIndex >= lastTopPickIndex && lastTopPickIndex !== -1) &&
                  !isLastResponse
                }
                icon={
                  <IconWrapper height="30px" width="30px" style={{ cursor: 'inherit' }}>
                    <ArrowRightSvg />
                  </IconWrapper>
                }
                onClick={() => {
                  if (isLastResponse) return;
                  onNext?.();
                }}
              />
            </Tooltip>
          </NavigationButtonsContainer>
        )}
        {responses && responses.length === 1 && (
          <NavigationButtonsContainer>
            <DoneReviewingButton onClick={onDone} $isActive={true}>
              <span>
                <T keyName="close" defaultValue="Close" />
              </span>
            </DoneReviewingButton>
          </NavigationButtonsContainer>
        )}
      </BodyContainer>
    </ModalWrapper>
  );
};

export default StrengthResponseReviewView;

const ModalWrapper = styled(Modal)`
  max-width: 100vw;
  .ant-modal-content {
    border-radius: 10px;
    box-shadow: none;
    margin: 0;
    color: ${theme.colors.white};
    width: 100vw;
  }
  .ant-modal-body {
    background: ${theme.colors.blackPure};
    box-shadow: none;
    height: 100vh;
  }
`;

const BodyContainer = styled.div`
  position: relative;
  padding: 20px;
  padding-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  height: 100%;
`;

const Title = styled.h1`
  font-size: 36px;
  color: ${theme.colors.white};
  margin-bottom: 20px;
  font-weight: 800;
  margin-top: 60px;
`;

const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  height: 80%;
`;

const SplitLayout = styled.div`
  width: 100%;
  padding: 20px;
  margin-top: 10px;
  margin-bottom: 10px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  height: 100%;
  min-height: 0;
`;

const CenteredImageContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  height: 100%;
`;

const CenteredResponseWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const ResponseCardContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${theme.colors.white};
  border-radius: 10px;
  overflow: hidden;
  :after {
    content: '';
    position: absolute;
    bottom: 0px;
    left: 0px;
    width: 100%;
    height: 40px;
    background: linear-gradient(
      to bottom,
      ${theme.colors.white}00,
      ${theme.colors.white}
    );
    pointer-events: none;
    z-index: 1;
  }
`;

const ResponseCard = styled.div`
  position: relative;
  color: ${theme.colors.black};
  border-radius: 10px;
  padding-top: 40px;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ResponseHeader = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  font-size: 16px;
  font-weight: bold;
  color: ${theme.colors.white};
  background: ${theme.colors.blue};
  padding: 5px 10px;
  border-radius: 10px 0px 10px 0px;
`;

const ResponseScrollInner = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  font-size: 28px;
  font-weight: 700;
  padding: 0 10px 20px;
  white-space: pre-wrap;
`;

const StrengthCardContainer = styled.div`
  position: relative;
  height: 100%;
  background: ${theme.colors.black};
  color: ${theme.colors.white};
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid ${theme.colors.translucentWhite};
  padding-top: 34px;
`;

const StrengthContent = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 20px;
  color: ${theme.colors.white};
  span {
    color: ${theme.colors.white};
    font-size: 28px;
  }
`;

const StrengthTabs = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  z-index: 3;
  border-radius: 10px 0px 10px 0px;
  overflow: hidden;
`;

const StrengthTab = styled.div`
  background: #9747ff;
  color: ${theme.colors.white};
  padding: 5px 10px;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
`;

const RevealOverlay = styled.div`
  background: ${theme.colors.red}07;
  backdrop-filter: blur(10px);
  position: absolute;
  inset: 0;
  z-index: 2;
  cursor: pointer;
`;

const RevealContent = styled.div`
  position: absolute;
  top: calc(50% - 40px);
  left: 50%;
  transform: translate(-50%, -50%);
  display: grid;
  justify-content: left;
  align-items: center;
  gap: 0px;
  height: fit-content;
  span {
    font-size: 40px;
    text-align: center;
    color: ${theme.colors.white};
  }
`;

const GreatPickTag = styled.div`
  position: absolute;
  top: 3px;
  right: 3px;
  z-index: 2;
  height: 30px;
  width: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 30px;
    height: 30px;
    color: ${theme.colors.betaYellow};
  }
`;

const HighlightSpan = styled.span`
  background-color: ${theme.colors.translucentOrange};
  color: ${theme.colors.black};
  padding: 2px 4px;
  border-radius: 3px;
  font-weight: bold;
`;

const NavigationButtonsContainer = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: flex;
  gap: 10px;
  z-index: 10;
  background: ${theme.colors.translucentLightBlack};
  padding: 10px;
  border-radius: 10px;
`;

const NavigationButton = styled(Button)<{ $isDisabled: boolean; $isOrange?: boolean }>`
  width: 130px;
  height: 40px;
  border-radius: 5px;
  background-color: ${({ $isOrange, $isDisabled }) =>
    $isOrange && !$isDisabled ? '#ff8c42' : theme.colors.greyDark};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'default' : 'pointer')};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.4 : 1)};
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: ${({ $isDisabled, $isOrange }) => {
      if ($isDisabled) return theme.colors.greyDark;
      if ($isOrange) return '#ff7a29';
      return theme.colors.iconGrey;
    }};
    transform: ${({ $isDisabled }) => ($isDisabled ? 'none' : 'scale(1.05)')};
    color: ${theme.colors.white};
  }

  &:active {
    transform: scale(0.95);
    background-color: ${theme.colors.iconGrey};
    color: ${theme.colors.white};
  }

  &:focus {
    background-color: ${theme.colors.iconGrey};
    color: ${theme.colors.white};
  }

  &:disabled {
    cursor: default;
  }

  svg {
    height: 30px;
    width: 30px;
  }
`;

const DoneReviewingButton = styled(Button)<{ $isActive: boolean }>`
  width: 160px;
  height: 40px;
  border-radius: 5px;
  background-color: ${({ $isActive }) => ($isActive ? '#ff8c42' : theme.colors.greyDark)};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
  font-weight: 600;

  span {
    color: ${theme.colors.white};
    font-size: 14px;
  }

  &:hover {
    background-color: ${({ $isActive }) =>
      $isActive ? '#ff7a29' : theme.colors.iconGrey};
    transform: scale(1.05);
    color: ${theme.colors.white};
  }

  &:active {
    transform: scale(0.95);
    background-color: #ff6810;
    color: ${theme.colors.white};
  }

  &:focus {
    background-color: #ff7a29;
    color: ${theme.colors.white};
  }
`;
